import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import { FileUp, Upload, X } from "lucide-react";
import { yetkiliMi, type GelenSatiri } from "@oasis/domain";
import { Band, Buton, Etiket, Hata, Sayfa } from "@/components/parcalar";
import { BelgeKarti, DurumHapi, UyariCipi, uyariKoduRengi } from "@/components/belge-karti";
import { GerekceAlani } from "@/components/belge-dosyala";
import { DenetimGorunumu } from "@/components/denetim-gorunumu";
import { DENETIM_UCU, hashGorunumu } from "@/lib/belge-denetimi";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { useOturum } from "@/lib/oturum";
import { dosyaYukle, tarih, useVeri } from "@/lib/veri";
import {
  KABUL_UZANTILARI,
  SUZGEC_SIRASI,
  acilisSuzgeci,
  baglantiMetni,
  dosyaAltMetni,
  durumAltMetni,
  hedefEkrani,
  incelemeMetni,
  ipucuMetni,
  kayitliSuzgec,
  listeBasligi,
  listeYolu,
  siraMetni,
  suzgecAdi,
  suzgecKaydet,
  uyariKaydi,
  uyariMetni,
  uyarilariSuz,
  yuklemeIlerlemesi,
  yuklemeOnDenetimi,
  yuklemeOzeti,
  yerCevir,
  type BelgeUyarisi,
  type ListeYaniti,
  type Suzgec,
  type YazimYaniti,
} from "@/lib/belgeler";

/**
 * D-074 Faz 1 (U-073): "Documents" sayfası (Faz 2: hidrotest sertifikası okunur, değerleri kartta gösterilir). Oturum açmış herkes dosya bırakır (K26); OASIS her dosyayı hemen saklar,
 * aynı dosya OASIS'te zaten varsa söyler (GEN-01), Word'ü saklar ama dosyalamaz (GEN-16). Kişi dosyanın ne olduğunu ve nereye ait
 * olduğunu seçer; yetkili kişi onu iş ekranlarının bugün dosyaladığı yere dosyalar (kart: components/belge-karti.tsx). Dönüştürücülü
 * türler "Send to converter" ile bugünkü toplu kapılara gider. Alerts görünümü okumasız kayıt uyarılarını (KAL-04, NDT-01, WPQ-08,
 * HT-10) ve Word satırlarını listeler; "Mark reviewed" kalite lideri ya da PM'in gerekçeli kararıdır (K3).
 * D-074 Faz 2 (K46): arama kutusu Alerts'te de görünür ve uyarıları istemcide süzer.
 * D-074 Faz 2 · PR 9: "Waiting" süzgeci (riserını, adımını ya da yerinin seçilmesini bekleyen okunmuş dosyalar; K31).
 * D-074 Faz 2 · PR 12 (K19): "Audit" sekmesi (dosyalanmış belgelerin denetimi; components/denetim-gorunumu.tsx). Süzgeçlerden ayrı bir `nav`'dır
 * (`SUZGEC_SIRASI` değişmez), yalnız `belgeUyari` rolüne görünür; sayısı açık bulgulu denetim kaydı sayısıdır; "#denetim" bağlantısı açar. Seçim tarayıcıda
 * hatırlanmaz.
 * Tasarım: prototip/sayfalar/belgeler--faz1*.html. Dar ekranda satırlar etiketli kartlara döner; sayfa yatay kaymaz.
 */

/**
 * Ekran denemesi bulgusu (U-073): tablo düzeni ekranın değil listenin genişliğiyle açılır (tasarım `.bl-satirlar`: kapsayıcı 54rem
 * altında etiketli kartlar). Önceden `md:` (768 px ekran) kullanılıyordu: beş sütunun en az genişliği ~860 px olduğundan 820 px tablette
 * ve kenar çubuğu açık 1024-1100 px ekranda sayfa yatay kayıyor, Alerts sütunu kesiliyordu.
 */
const SATIR_IZGARASI = "grid grid-cols-2 gap-x-3 gap-y-2 @min-[54rem]:grid-cols-[minmax(220px,2.2fr)_minmax(140px,1.1fr)_minmax(170px,1.4fr)_minmax(130px,1fr)_minmax(120px,.9fr)] @min-[54rem]:items-center @min-[54rem]:gap-3";
const HUCRE_ETIKETI = "mb-px block text-[11px] font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[54rem]:hidden";
const ALT = "block text-xs font-normal text-ink-3 [overflow-wrap:anywhere]";

/**
 * Dosya bırakma alanı ve yükleme turu: dosyalar sırayla tek tek gider (her biri ayrı başarır ya da reddedilir). İnceleme bulgusu
 * (U-073): yükleme sürerken bırakılan dosyalar sıraya eklenir (önceden sessizce yok sayılıyordu); sayfanın başka bir yerine bırakılan
 * dosya da yüklenir (önceden tarayıcı PDF'i açıp OASIS'ten çıkıyordu, sayfa düzeyindeki dinleyici `Belgeler`'de).
 */
function BirakmaAlani({ secici, onDosyalar }: { secici: React.RefObject<HTMLInputElement | null>; onDosyalar: (f: File[]) => void }) {
  const [ustunde, setUstunde] = useState(false);
  const birak = (e: DragEvent) => {
    e.preventDefault();
    setUstunde(false);
    if (e.dataTransfer.files.length) onDosyalar([...e.dataTransfer.files]);
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setUstunde(true); }}
      onDragLeave={() => setUstunde(false)}
      onDrop={birak}
      className={cn("m-3 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border-2 border-dashed px-4 py-[18px]", ustunde ? "border-orange! bg-orange-soft" : "border-line-strong! bg-surface-2")}
    >
      <span className="inline-flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[10px] bg-surface text-orange"><FileUp size={22} /></span>
      <div className="min-w-0 flex-[1_1_260px]">
        <div className="text-md font-semibold">{cevir("Drop files here or choose files")}</div>
        <div className="text-xs text-ink-3">{cevir("PDF, images (JPG, PNG) and the excel files of the converter types · several files at once · up to 100 MB each. Word is not accepted for reports: upload the signed PDF.")}</div>
      </div>
      <Buton onClick={() => secici.current?.click()}>{cevir("Choose files")}</Buton>
    </div>
  );
}

/** Listenin bir satırı (dar ekranda etiketli kart). Maskeli satırda yalnız "Personal document · uploaded" ve durum. */
function ListeSatiri({ s, acik, onAc }: { s: GelenSatiri; acik: boolean; onAc: () => void }) {
  const maskeli = s.gorunum === "yuklendi";
  const alt = durumAltMetni(s);
  const tire = <span className="text-ink-3">—</span>;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={acik}
      onClick={onAc}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAc(); } }}
      className={cn(SATIR_IZGARASI, "cursor-pointer border-t border-line px-4 py-2.5 text-base hover:bg-surface-2/60 [&>*]:min-w-0", acik && "bg-surface-2/60")}
    >
      <div className="col-span-2 @min-[54rem]:col-span-1">
        <span className="font-semibold [overflow-wrap:anywhere]">{maskeli ? (s.turAdi ? cevir(s.turAdi) : "—") : s.ad}</span>
        <span className={ALT}>{maskeli ? cevir("uploaded {0}", tarih(s.ts)) : dosyaAltMetni(s)}</span>
      </div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Type")}</span>
        {!maskeli && s.turAdi ? cevir(s.turAdi) : tire}
        {!maskeli && !s.tur && s.ipucu && <span className={ALT}>{cevir("From the file name (a hint only): {0}", ipucuMetni(s.ipucu))}</span>}
      </div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Owner")}</span>
        {!maskeli && s.hedefMetni ? <>{yerCevir(s.hedefMetni)}{hedefEkrani(s.hedef) && <span className={ALT}>{hedefEkrani(s.hedef)}</span>}</> : tire}
      </div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Status")}</span>
        <DurumHapi s={s} sar />
        {alt && <span className={ALT}>{alt}</span>}
      </div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Alerts")}</span>
        {maskeli ? tire : s.uyarilar.length ? <span className="flex flex-wrap gap-1">{s.uyarilar.map((u) => <UyariCipi key={u.kod} kod={u.kod} renk={u.renk} />)}</span> : <span className="text-ink-3">{cevir("none")}</span>}
      </div>
    </div>
  );
}

/**
 * Uyarı tablosunun sütunları (tasarım `.bl-uyari-satir`; ekran denemesi bulgusu, U-073): kapsayıcı 48rem ve üstünde tablo, altında
 * "kod · tarih / cümle / kayıt / durum". Satırın solunda tonun 4 px çizgisi: açık sarı, Word (GEN-16) kırmızı, incelenmiş gri; kod tonun
 * renginde; seçili satır sarı zeminli. Önceden çizgi ve kod rengi yoktu, düzen 768 px ekrana bağlıydı.
 */
const UYARI_SUTUNLARI_SECIMLI = "@min-[48rem]:grid-cols-[28px_76px_minmax(0,2fr)_minmax(0,1.6fr)_96px_minmax(0,1.3fr)]";
const UYARI_SUTUNLARI = "@min-[48rem]:grid-cols-[76px_minmax(0,2fr)_minmax(0,1.6fr)_96px_minmax(0,1.3fr)]";

/**
 * Documents › Alerts: kayıt uyarıları ve Word satırları; seçilenler tek gerekçeyle "Mark reviewed" (kalite lideri, PM).
 * D-074 Faz 2 · PR 8 (T26): okunan dosyaların saklı sarı ve kırmızı uyarıları da gelir ("uyari:<id>"). Kırmızı seçilemez ("Needs decision"; karar
 * kartta verilir), sarı toplu "Mark reviewed"e girer; satırı olan her uyarının kayıt hücresi kartı açar.
 */
function UyariGorunumu({ onKarta, ust, onDegisti, ara, sag }: { onKarta: (gelenId: string) => void; ust: ReactNode; onDegisti: () => Promise<void> | void; ara: string; sag?: ReactNode }) {
  const { ben } = useOturum();
  const [incelenen, setIncelenen] = useState(false);
  const { veri, hata, yenile } = useVeri<{ uyarilar: BelgeUyarisi[] }>(`/belgeler/uyarilar${incelenen ? "?incelenen=1" : ""}`);
  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [gerekce, setGerekce] = useState("");
  const [mesgul, setMesgul] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState<string | null>(null);
  const karar = Boolean(ben && yetkiliMi(ben.rol, "belgeUyari"));
  const uyarilar = useMemo(() => veri?.uyarilar ?? [], [veri]);
  /** K46: aramaya uyan uyarılar (istemcide); "Select all" yalnız bunları seçer, arama değişince seçim temizlenir. */
  const gorunen = useMemo(() => uyarilariSuz(uyarilar, ara), [uyarilar, ara]);
  useEffect(() => { setSecili(new Set()); }, [ara]);
  // GEN-16 da kırmızıdır: kırmızı uyarı yalnız kartta kararlanır.
  const secilebilir = gorunen.filter((u) => !u.karar && u.renk !== "kirmizi");
  const hepsi = secilebilir.length > 0 && secilebilir.every((u) => secili.has(u.anahtar));
  /** Seçim kutusu sütunu yalnız karar verebilen kişide (saha inspektöründe boş 28 px sütun kalıyordu; ekran denemesi bulgusu). */
  const secimSutunu = karar;
  const degistir = (anahtar: string, acik: boolean) => setSecili((s) => { const y = new Set(s); if (acik) y.add(anahtar); else y.delete(anahtar); return y; });

  const incele = async () => {
    if (gerekce.trim().length < 3) { setIslemHatasi(cevir("Write the reason.")); return; }
    setMesgul(true);
    setIslemHatasi(null);
    try {
      await api.post<{ yazilan: string[]; acikDegil: string[] }>("/belgeler/uyarilar/incele", { anahtarlar: [...secili], gerekce: gerekce.trim() });
      setSecili(new Set());
      setGerekce("");
      // Ekran denemesi bulgusu (U-073): süzgeçteki "Alerts" sayısı da tazelenir (önceden sayfa yenilenene kadar eski sayı kalıyordu).
      await Promise.all([yenile(), onDegisti()]);
    } catch (e) {
      setIslemHatasi(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">
        {ust}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={incelenen} onChange={(e) => setIncelenen(e.target.checked)} /> {cevir("Show reviewed")}</label>
        {sag}
      </div>
      {karar && secili.size > 0 && (
        <div className="border-b border-line px-4 py-3">
          {/* Tasarım `.bl-inceleme`: sarı kenarlı kutu (ekran denemesi bulgusu; önceden gri şeritti). */}
          <div className="space-y-2 rounded-lg border border-warn! bg-surface p-2.5">
            <div className="flex flex-wrap items-center gap-2"><span className="text-md font-semibold">{cevir("Mark reviewed")}</span><span className="text-xs text-ink-3">{cevir("{0} selected", secili.size)}</span></div>
            <GerekceAlani id="belge-uyari-gerekce" deger={gerekce} onChange={setGerekce} zorunlu disabled={mesgul} />
            <Hata mesaj={islemHatasi} />
            <div className="flex flex-wrap gap-2">
              <Buton tur="birincil" islem="belgeUyari" mesgul={mesgul} onClick={incele}>{cevir("Mark reviewed")}</Buton>
              <Buton disabled={mesgul} onClick={() => { setSecili(new Set()); setGerekce(""); setIslemHatasi(null); }}>{cevir("Cancel")}</Buton>
            </div>
          </div>
        </div>
      )}
      <Hata mesaj={hata} />
      {!veri && !hata ? <p className="px-4 py-3 text-sm text-ink-3">…</p> : !uyarilar.length ? (
        <p className="border-b border-line px-4 py-3.5 text-base text-ink-2">{cevir("No open alerts.")}</p>
      ) : !gorunen.length ? (
        <p className="border-b border-line px-4 py-3.5 text-base text-ink-2">{cevir("No alerts match the search.")}</p>
      ) : (
        <div className="@container">
          <div className={cn("hidden gap-3 border-l-4 border-l-transparent! bg-surface-2 py-2 pl-3 pr-4 text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[48rem]:grid", secimSutunu ? UYARI_SUTUNLARI_SECIMLI : UYARI_SUTUNLARI)}>
            {secimSutunu && <span>{secilebilir.length > 0 && <input type="checkbox" aria-label={cevir("Select all")} checked={hepsi} onChange={(e) => setSecili(e.target.checked ? new Set(secilebilir.map((u) => u.anahtar)) : new Set())} />}</span>}
            <span>{cevir("Code")}</span><span>{cevir("Alert")}</span><span>{cevir("Record")}</span><span>{cevir("Since")}</span><span>{cevir("Status")}</span>
          </div>
          {gorunen.map((u) => {
            const secim = karar && !u.karar && u.renk !== "kirmizi";
            const ton = u.karar ? "incelendi" : u.renk;
            return (
              <div
                key={u.anahtar}
                className={cn(
                  "grid gap-x-3 gap-y-1 border-l-4 border-t border-line py-2.5 pl-3 pr-4 text-sm @min-[48rem]:items-baseline",
                  secimSutunu ? cn("grid-cols-[28px_minmax(0,1fr)_auto]", UYARI_SUTUNLARI_SECIMLI) : cn("grid-cols-[minmax(0,1fr)_auto]", UYARI_SUTUNLARI),
                  ton === "incelendi" ? "border-l-line-strong! text-ink-2" : ton === "kirmizi" ? "border-l-bad!" : "border-l-warn!",
                  secili.has(u.anahtar) && "bg-warn-bg",
                )}
              >
                {secimSutunu && <span className="row-span-4 @min-[48rem]:row-span-1">{secim && <input type="checkbox" aria-label={cevir("Select")} checked={secili.has(u.anahtar)} onChange={(e) => degistir(u.anahtar, e.target.checked)} />}</span>}
                <span className={cn("order-1 font-mono text-xs font-semibold @min-[48rem]:order-none", ton === "incelendi" ? "text-ink-3" : uyariKoduRengi(u.renk))}>{u.kod}</span>
                <span className="order-3 col-span-2 @min-[48rem]:order-none @min-[48rem]:col-span-1">{uyariMetni(u)}</span>
                <span className="order-4 col-span-2 text-ink-2 [overflow-wrap:anywhere] @min-[48rem]:order-none @min-[48rem]:col-span-1 @min-[48rem]:text-inherit">
                  {u.gelenId
                    ? <button type="button" className="text-left text-orange underline" onClick={() => onKarta(u.gelenId!)}>{uyariKaydi(u)}</button>
                    : uyariKaydi(u)}
                </span>
                <span className="tnum order-2 text-right text-ink-2 @min-[48rem]:order-none @min-[48rem]:text-left">{tarih(`${u.tarih}T12:00:00.000Z`)}</span>
                <span className="order-5 col-span-2 @min-[48rem]:order-none @min-[48rem]:col-span-1">
                  {u.karar ? <span className="text-xs text-ink-3">{incelemeMetni(u.karar)}</span> : u.renk === "kirmizi" ? <Etiket tone="bad">{cevir("Needs decision")}</Etiket> : <Etiket tone="warn">{cevir("open")}</Etiket>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

type YuklemeDurumu = { sira: number; toplam: number; ad: string };
type YuklemeOzeti = { yuklenen: number; reddedilen: number; mesajlar: string[] };

export function Belgeler() {
  const { ben } = useOturum();
  const [secilen, setSecilen] = useState<Suzgec | null>(() => kayitliSuzgec());
  const [ara, setAra] = useState("");
  const [aranan, setAranan] = useState("");
  useEffect(() => {
    const z = setTimeout(() => setAranan(ara), 300);
    return () => clearTimeout(z);
  }, [ara]);
  const suzgec: Suzgec = secilen ?? "tumu";
  const { veri, hata, yenile } = useVeri<ListeYaniti>(listeYolu(suzgec, aranan));
  const [ekler, setEkler] = useState<GelenSatiri[]>([]);
  const [sonraki, setSonraki] = useState<string | null>(null);
  const [acikId, setAcikId] = useState<string | null>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [yukleme, setYukleme] = useState<YuklemeDurumu | null>(null);
  const [ozet, setOzet] = useState<YuklemeOzeti | null>(null);
  const secici = useRef<HTMLInputElement | null>(null);
  /** Açık kartın satırı (inceleme bulgusu, U-073): dosyalanan ya da gönderilen satır süzgeçten çıksa da kart kapanana kadar yerinde kalır. */
  const [sabit, setSabit] = useState<GelenSatiri | null>(null);
  const konum = useLocation();
  /** D-074 Faz 2 · PR 12: "Audit" sekmesi (yalnız belgeUyari). */
  const denetimYetkisi = ben ? yetkiliMi(ben.rol, "belgeUyari") : false;
  // "#denetim" bağlantısı ilk çizimde de açar (zilden ve paylaşılan bağlantıdan gelen); süzgeç düğmesi kapatır.
  const [denetimAcik0, setDenetimAcik] = useState(() => String(konum.hash ?? "").replace(/^#/, "") === "denetim");
  const denetimAcik = denetimAcik0 && denetimYetkisi;
  const denetimSayi = useVeri<{ acikBulgu: number }>(denetimYetkisi ? DENETIM_UCU.sayi : null);

  // Açılış: kişi süzgeç seçmediyse "Needs me" sayısı varsa o, yoksa All (seçim tarayıcıda hatırlanır). Ekran denemesi bulgusu (U-073):
  // karar yalnız ilk veriyle verilir; önceden All görünümünde dosya yükleyince sayfa kendiliğinden "Needs me"ye geçiyordu.
  const acilisSecildi = useRef(false);
  useEffect(() => {
    if (acilisSecildi.current || !veri) return;
    acilisSecildi.current = true;
    if (secilen === null) {
      const s = acilisSuzgeci(null, veri.sayilar.bende);
      if (s !== "tumu") setSecilen(s);
    }
  }, [veri, secilen]);
  useEffect(() => {
    setEkler([]);
    setSonraki(veri?.sonraki ?? null);
  }, [veri]);

  // Zilden gelen bağlantı (inceleme bulgusu, U-073): "#uyarilar" Alerts görünümünü, "#belge-<id>" satırın kartını açar; ikisi de Audit görünümünü kapatır.
  useEffect(() => {
    const g = hashGorunumu(konum.hash);
    if (!g) return;
    setDenetimAcik(g.denetimAcik);
    if (g.uyarilar) setSecilen("uyarilar");
    if (g.acikId) setAcikId(g.acikId);
  }, [konum.hash]);

  // Benim rezervasyonumda kalmış satırlar (aktarma bitti, işaret düşmedi): açılışta bir kez "dosyalandı" sorulur; kanıt karar verir.
  const denetlendi = useRef(false);
  useEffect(() => {
    if (denetlendi.current || !veri || !ben) return;
    denetlendi.current = true;
    const benimkiler = veri.satirlar.filter((s) => s.etiket === "dosyalaniyor" && s.dosyalayan?.ad === ben.ad);
    if (!benimkiler.length) return;
    void Promise.allSettled(benimkiler.map((s) => api.post<YazimYaniti>(`/belgeler/${s.id}/dosyalandi`))).then((r) => {
      if (r.some((x) => x.status === "fulfilled")) void yenile();
    });
  }, [veri, ben, yenile]);

  const sec = (s: Suzgec) => {
    setDenetimAcik(false);
    setSecilen(s);
    suzgecKaydet(s);
    setAcikId(null);
  };

  const listedekiler = useMemo(() => [...(veri?.satirlar ?? []), ...ekler], [veri, ekler]);
  /** Görünen satırlar: liste ve, süzgeçten çıkmışsa, açık kartın satırı (zamanına göre yerinde). */
  const satirlar = useMemo(() => {
    if (!acikId || !sabit || sabit.id !== acikId || listedekiler.some((x) => x.id === acikId)) return listedekiler;
    return [...listedekiler, sabit].sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : a.id < b.id ? 1 : -1));
  }, [listedekiler, acikId, sabit]);
  const ac = (s: GelenSatiri | null) => {
    setAcikId(s?.id ?? null);
    setSabit(s);
  };
  const dahaFazla = async () => {
    if (!sonraki) return;
    const y = await api.get<ListeYaniti>(listeYolu(suzgec, aranan, sonraki));
    setEkler((e) => [...e, ...y.satirlar]);
    setSonraki(y.sonraki);
  };

  /**
   * Seçilen ya da bırakılan dosyalar sırayla yüklenir; sonunda "{0} uploaded · {1} refused" ve her cümle dosyanın adıyla (inceleme
   * bulgusu, U-073: on dosyalık bir bırakmada hangi dosyanın kopya olduğu ya da reddedildiği görünmüyordu). Yükleme sürerken gelen
   * dosyalar aynı turun sırasına eklenir.
   */
  const kuyruk = useRef<File[]>([]);
  const calisiyor = useRef(false);
  const yukle = async (dosyalar: File[]) => {
    kuyruk.current.push(...dosyalar);
    if (calisiyor.current) return;
    calisiyor.current = true;
    setOzet(null);
    setMesaj(null);
    const o: YuklemeOzeti = { yuklenen: 0, reddedilen: 0, mesajlar: [] };
    const adli = (f: File, m: string) => (m.includes(f.name) ? m : `${f.name}: ${m}`);
    let son: string | null = null;
    let sira = 0;
    try {
      for (let f = kuyruk.current.shift(); f; f = kuyruk.current.shift()) {
        sira++;
        setYukleme({ sira, toplam: sira + kuyruk.current.length, ad: f.name });
        const on = yuklemeOnDenetimi(f);
        if (on) { o.reddedilen++; o.mesajlar.push(adli(f, on)); continue; }
        try {
          const y = await dosyaYukle<{ satir: GelenSatiri | null; yeni: boolean; mesaj?: string }>("/belgeler/yukle", f);
          o.yuklenen++;
          if (y.mesaj) o.mesajlar.push(adli(f, cevir(y.mesaj)));
          son = y.satir?.id ?? son;
        } catch (e) {
          o.reddedilen++;
          o.mesajlar.push(adli(f, e instanceof Error ? e.message : cevir("Upload failed")));
        }
      }
    } finally {
      calisiyor.current = false;
    }
    setYukleme(null);
    setOzet(o);
    if (sira === 1 && son) {
      // Yüklenen dosyanın kartı açılır; Audit görünümü açıksa kapanır (kart Documents listesindedir).
      setDenetimAcik(false);
      setSabit(null);
      setAcikId(son);
    }
    await yenile();
  };
  // Sayfanın herhangi bir yerine bırakılan dosya yüklenir; bırakma alanının kendi bırakması `defaultPrevented` ile ayrılır.
  const yukleRef = useRef(yukle);
  yukleRef.current = yukle;
  useEffect(() => {
    const dosyaMi = (e: globalThis.DragEvent) => Boolean(e.dataTransfer && [...e.dataTransfer.types].includes("Files"));
    const uzerinde = (e: globalThis.DragEvent) => { if (dosyaMi(e)) e.preventDefault(); };
    const birak = (e: globalThis.DragEvent) => {
      if (!dosyaMi(e) || e.defaultPrevented) return;
      e.preventDefault();
      const f = [...(e.dataTransfer?.files ?? [])];
      if (f.length) void yukleRef.current(f);
    };
    window.addEventListener("dragover", uzerinde);
    window.addEventListener("drop", birak);
    return () => {
      window.removeEventListener("dragover", uzerinde);
      window.removeEventListener("drop", birak);
    };
  }, []);

  const karta = (gelenId: string) => {
    sec("tumu");
    setAcikId(gelenId);
  };

  const suzgecNav = (
    // Ekran denemesi bulgusu (U-073): dar ekranda süzgeçler alt satıra sarar (tasarım `.sk-filtre`); önceden kaydırma çubuğu görünmeyen yatay kaydırmaydı, son süzgeç kesik görünüyordu.
    <nav aria-label={cevir("Filter")} className="max-w-full">
      <div className="inline-flex max-w-full flex-wrap gap-1 rounded-[10px] bg-surface-2 p-[3px]">
        {SUZGEC_SIRASI.map((s) => {
          const secili = !denetimAcik && s === suzgec;
          return (
            <button key={s} type="button" aria-current={secili || undefined} onClick={() => sec(s)} className={cn("whitespace-nowrap rounded-lg px-2.5 py-1 text-sm text-ink-2", secili && "bg-surface font-semibold text-ink shadow-[var(--shadow)]")}>
              {suzgecAdi(s)} <span className="tnum">{veri?.sayilar[s] ?? ""}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
  /** D-074 Faz 2 · PR 12 (J17): "Audit" ayrı bir nav'dır; yalnız belgeUyari rolünde. */
  const denetimNav = denetimYetkisi ? (
    <nav aria-label={cevir("Audit")} className="max-w-full">
      <div className="inline-flex rounded-[10px] bg-surface-2 p-[3px]">
        <button type="button" aria-current={denetimAcik || undefined} onClick={() => { setDenetimAcik(true); setAcikId(null); }} className={cn("whitespace-nowrap rounded-lg px-2.5 py-1 text-sm text-ink-2", denetimAcik && "bg-surface font-semibold text-ink shadow-[var(--shadow)]")}>
          {cevir("Audit")} <span className="tnum">{denetimSayi.veri?.acikBulgu ?? ""}</span>
        </button>
      </div>
    </nav>
  ) : null;
  /* K46: kutu Alerts'te de görünür (aynı metin); Alerts'te uyarıları istemcide süzer, öteki süzgeçlerde sunucuda arar. */
  const aramaKutusu = <input className="h-[34px] min-w-0 max-w-[380px] flex-[1_1_220px] rounded-lg border border-line-strong bg-bg px-2.5 text-base text-ink outline-none placeholder:text-ink-3 focus:border-orange" placeholder={cevir("Search file, riser, SN or register entry...")} value={ara} onChange={(e) => setAra(e.target.value)} aria-label={cevir("Search file, riser, SN or register entry...")} />;
  const baslik = <span className="text-md font-semibold">{listeBasligi(suzgec)}</span>;
  const filtreler = <>{baslik}{suzgecNav}{denetimNav}{aramaKutusu}</>;

  const acikSatir = acikId ? satirlar.find((s) => s.id === acikId) ?? null : null;
  const kart = (s: GelenSatiri) => (
    <div className="border-t border-line bg-surface-2/40 p-3">
      <BelgeKarti key={s.id} ozet={s} onYenile={yenile} onKapat={() => ac(null)} onMesaj={setMesaj} onSatir={(g) => { if (g.id === acikId) setSabit(g); }} />
    </div>
  );
  /** Kartı listede olmayan satır (Alerts'ten açılan Word satırı, yeni yükleme) için: kart yanıtı satırı getirir. */
  const disKart = acikId && !acikSatir ? <DisKart id={acikId} onYenile={yenile} onKapat={() => ac(null)} onMesaj={setMesaj} /> : null;

  return (
    <Sayfa
      ust={cevir("All uploads in one place")}
      baslik={cevir("Documents")}
      alt={cevir("Drop any file here. OASIS keeps it at once and shows when the same file is already in OASIS. Choose what the file is and where it belongs; a person with the right role files it where the work screens file it today. Nothing is lost: every file stays in the list.")}
      aksiyon={<Buton tur="birincil" onClick={() => secici.current?.click()}><Upload size={15} />{cevir("Upload document")}</Buton>}
    >
      <input ref={secici} type="file" multiple accept={KABUL_UZANTILARI} className="hidden" onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ""; if (f.length) void yukle(f); }} />
      {mesaj && (
        <Band tip="bilgi" className="flex items-start gap-2">
          <span className="min-w-0 flex-1">{mesaj}</span>
          <button type="button" onClick={() => setMesaj(null)} className="shrink-0 rounded p-0.5 hover:bg-surface-2" aria-label={cevir("Close")}><X size={14} /></button>
        </Band>
      )}
      <section className="min-w-0 rounded-lg border border-line bg-surface shadow-[var(--shadow)]">
        <BirakmaAlani secici={secici} onDosyalar={(f) => void yukle(f)} />
        <div role="status" aria-live="polite">
        {yukleme && <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-line px-4 py-2.5 text-sm text-ink-2"><span className="[overflow-wrap:anywhere]">{yuklemeIlerlemesi(yukleme.sira, yukleme.toplam, yukleme.ad)}</span></div>}
        {ozet && (
          <div className="space-y-1 border-t border-line px-4 py-2.5 text-sm">
            <p className="font-medium">{yuklemeOzeti(ozet.yuklenen, ozet.reddedilen)}</p>
            {ozet.mesajlar.map((m, i) => <p key={i} className="text-ink-2 [overflow-wrap:anywhere]">{m}</p>)}
          </div>
        )}
        </div>
        {veri && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-line px-4 py-2.5 text-sm text-ink-2">
            <span className="tnum">{baglantiMetni(veri.serit)}</span>
            <span>{siraMetni(veri.serit)}</span>
          </div>
        )}
      </section>

      <section className="min-w-0 rounded-lg border border-line bg-surface shadow-[var(--shadow)]">
        {denetimAcik ? (
          <DenetimGorunumu ust={<>{suzgecNav}{denetimNav}</>} onDegisti={denetimSayi.yenile} />
        ) : suzgec === "uyarilar" ? (
          // Alerts'te "Audit" sekmesi "Show reviewed"ın ardındadır (süzgeç satırının metni değişmesin).
          <UyariGorunumu onKarta={karta} ust={<>{baslik}{suzgecNav}{aramaKutusu}</>} onDegisti={yenile} ara={ara} sag={denetimNav} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">{filtreler}</div>
            <Hata mesaj={hata} />
            {disKart && <div className="border-b border-line bg-surface-2/40 p-3">{disKart}</div>}
            {veri && !satirlar.length ? (
              <p className="px-4 py-3.5 text-base text-ink-2">{suzgec === "bende" ? cevir("Nothing is waiting for you. A file you upload appears here first; it moves to Filed when it is confirmed.") : suzgec === "bekleyen" ? cevir("No file is waiting for its riser, its step or an owner.") : cevir("No documents.")}</p>
            ) : (
              <div className="@container">
                <div className={cn(SATIR_IZGARASI, "hidden bg-surface-2 px-4 py-2 text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[54rem]:grid")}>
                  <span>{cevir("File")}</span><span>{cevir("Type")}</span><span>{cevir("Owner")}</span><span>{cevir("Status")}</span><span>{cevir("Alerts")}</span>
                </div>
                {satirlar.map((s) => (
                  <div key={s.id}>
                    <ListeSatiri s={s} acik={acikId === s.id} onAc={() => ac(acikId === s.id ? null : s)} />
                    {acikId === s.id && kart(s)}
                  </div>
                ))}
                {sonraki && <div className="border-t border-line px-4 py-2.5"><Buton kucuk onClick={dahaFazla}>{cevir("Show more")}</Buton></div>}
              </div>
            )}
          </>
        )}
      </section>

      <Band tip="bilgi">{cevir("Red stops filing: the file waits in “Needs decision” until the Quality Lead or PM decides, with a reason. Amber: the alert stays open until the Quality Lead or PM marks it reviewed. Grey is shown once. Work screens get no new blocks: today's walls stay as they are.")}</Band>
    </Sayfa>
  );
}

/** Listede görünmeyen bir satırın kartı: satır kart yanıtından gelir (görünmüyorsa sunucu 404 der, kart kapanır). */
function DisKart({ id, onYenile, onKapat, onMesaj }: { id: string; onYenile: () => Promise<void>; onKapat: () => void; onMesaj: (m: string) => void }) {
  const { veri, hata } = useVeri<{ satir: GelenSatiri }>(`/belgeler/${id}`);
  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return null;
  return <BelgeKarti key={id} ozet={veri.satir} onYenile={onYenile} onKapat={onKapat} onMesaj={onMesaj} />;
}
